import { connect } from 'react-redux';
import { ApplicationState } from "../../../store";
import { ToxnotComponentProperties } from "../BaseApplicationComponents/ToxnotComponent";
import * as SurveyResponseController from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";
import { SurveyResponseState } from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseStates';
import { TeamRole } from '../../../store/Models/UserContext';
import { ToxnotMainApplicationViewComponent, ViewOptions } from '../BaseApplicationComponents/ToxnotMainApplicationViewComponent';
import { GroupedResponseInventories, SurveyResponseInventoryGroup } from '../../../store/Models/Surveys/SurveyDataModel';
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from '../ToxnotReactModal';
import { LoadingSpinner } from '../LoadingSpinner';
import CheckBox from '../CheckBox';
import { SimpleInventory } from '../../../store/Models/Inventories/SimpleInventory';
import SearchableDropdown from '../SearchableDropdown/SearchableDropdown';
import * as SurveyStateController from "../../../store/StateControllers/Survey/SurveyStateController";

import './styles.css';
import { CoreSurveyResponseData } from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import {
    coreValues,
    surveyResponseMaterialsPerPage
} from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";

export type Props =
    ToxnotComponentProperties &
    typeof SurveyResponseController.actionCreators &
    SurveyResponseState;

enum UiState {
    Loading,
    Failed,
    Loaded
}

interface LocalState {
    uiState: UiState;
    selectedOriginalInventoryIds: Set<string>;
    groupName: string | null;
    allSelected: boolean;
    selectedGroup: SurveyResponseInventoryGroup | null;
    filteredMaterialList: SimpleInventory[];
    isMaterialListFiltered: boolean;
    materialSearchTerm: string;
    allGroupedData: CoreSurveyResponseData;
    isItemRemoved: boolean;
}

// ===============================================================================

//
// Main component for rendering the "materials grouping" step in a survey response. The user can take materials in the
// survey request and organize them into named groups.
//
// The named groups then become the actual materials that are filled out in the survey response.
//
// And the survey response, when submitted, will copy the data from the group materials into the original materials
// that were requested.
//
// This is used by suppliers when they have a lot of materials in a survey that all have the same underlying data. So
// the supplier can just fill out the data for all of them as one material.
//
class GroupMaterials extends ToxnotMainApplicationViewComponent<Props, UiState, LocalState, GroupedResponseInventories> {
    original = SurveyResponseController.getOriginalRequestMaterialInventoryList();
    
    public constructor(props: Props) {
        super(props);
        this.state = {
            uiState: UiState.Loading,
            selectedOriginalInventoryIds: new Set<string>(),
            groupName: null,
            allSelected: false,
            selectedGroup: null,
            filteredMaterialList: [] as SimpleInventory[],
            isMaterialListFiltered: false,
            materialSearchTerm: "",
            allGroupedData: {} as CoreSurveyResponseData,  // Initialize as empty CoreSurveyResponseData object
            isItemRemoved: false
        };
    }

    // ===============================================================================

    async componentDidMount() {
        try {
            // Fetch grouped data
            const response = await this.getAllGroupedData();

            if (!response.success || !response.data) {
                this.setState({
                    uiState: UiState.Failed
                });
                return;
            }

            // Ensuring `response.data` exists and is matching the expected type
            const groupedData = response.data;
            
            this.setState({
                allGroupedData: groupedData,
            });

            this.original = groupedData.response.inventoryDataList
                .map(d => groupedData.inventoryInfoTable[d.responderOwnedResponseInventoryId] ?? null)
                .filter(inv => inv !== null)
                .orderBy(inv => inv.name);
            const filteredMaterialData = this.getOriginalMaterialList();


            this.setState({
                filteredMaterialList: filteredMaterialData,
                uiState: UiState.Loaded
            });

        } catch (error) {
            console.error('Error initializing component:', error);
        }
    }

    // ===============================================================================

    //
    // Set view options in base class. This is an embedded component and
    // doesn't need a full app layout around it.
    //
    protected getViewOptions(): Partial<ViewOptions> {
        return {
            renderAppLayout: false
        };
    }

    // ===============================================================================

    // Only editors can edit survey responses
    protected getDisallowedRoles(): TeamRole[] {
        return [TeamRole.Marketing, TeamRole.Viewer];
    }

    // ===============================================================================

    protected getPostInitialLoadUiState() {
        return UiState.Loaded;
    }

    // ===============================================================================

    protected async fetchViewDataAndRefreshState() {
        this.setState({
            uiState: UiState.Loaded
        });
    }

    // ===============================================================================

    //
    // not using the generic title
    //
    protected getLocalizedViewTitle(): string | null {
        return null;
    }

    // ===============================================================================

    protected async saveDataToService(groups: GroupedResponseInventories) {
        const apiResult = await this._serviceClient.dataCollectionResponseWriteGroupedResponseInventories(
            this.props.responseId,
            groups
        );

        if (!apiResult.success) {
            if (SurveyResponseController.isSupplierActionUnavailableResult(apiResult)) {
                this.props.setSupplierActionUnavailable(apiResult);
                return false;
            }

            this.props.appServices?.displayApiErrorMessage();
            this.hideModal("enter-material-group-name-modal");
            this.hideModal("edit-group-material-List");
            await this.props.getPaginatedSurveyData(this.props.navContext.currentPageOfMaterialsIndex);
            return false;
        }

        this.setGroupsState(apiResult.data!);

        return true;
    }

    // ===============================================================================
    private isNullOrEmpty = (value: string | null | undefined) => value === null || value === "" || value === undefined;

    // ===============================================================================

    //
    // Render main view
    //
    protected renderViewContent() {
        const groups = Object.values(this.state.allGroupedData.response.groupedResponseInventories);//Object.values(this.props.coreData!.response.groupedResponseInventories);
        const unnamedMaterial = this.localizeString("Unnamed Material");

        return (
            <div className="survey-response-content">
                <div className="m-b-10 page-title-border">
                    <h4 className="m-t-10 m-b-10">{this.localizeString("Material Groups")}<span className="badge m-l-5">{this.localizeString("Optional")}</span></h4>
                </div>
                <div className="instructions">
                    <div className="start-subcontent-heading">{this.localizeString("Did you get asked about two or more Materials that have the same answers ? If so, you can group these Materials together to create a Material Type.")}</div>
                    <p className="start-subcontent-content-container">
                        {this.localizeString('Example Scenario: The requestor asks for product data about two Materials, "Screw A" and "Screw B." If both Screws have the same answers, you can group them together and create a Material Type called "Screw." For the rest of the survey, you will only see one Material Type "Screw" instead of "Screw A" and "Screw B."')}
                    </p>
                </div>
                <div className="view-root tn-row">
                    <div id="original-material-container" className="col-xs-12 col-lg-6 m-b-20">
                        <div className="start-subcontent-heading">{this.localizeString("Select Materials with the same data")}</div>
                        <div className="start-subcontent-content-container row p-r-15">
                            <CheckBox checked={this.state.allSelected} onValueChange={(newValue: boolean) => this.onSelectAllChanged(newValue)}></CheckBox>
                            <input type="text" value={this.state.materialSearchTerm} className="form-control m-r-10" placeholder={this.localizeString("Search by Name, Product Number, Supplier Part #, or Description")} onChange={(e) => this.filterMaterialList(e.target.value)} />
                            <button type="button" className="btn btn-default form-control" name="create-group" onClick={e => this.addSelectedMaterialsToGroup_click()}>{this.localizeString("Add Selected Materials to a Group")}</button>
                        </div>
                        {this.state.filteredMaterialList.length === 0 && !this.state.isMaterialListFiltered
                            ? (
                                <div>{this.localizeString("There are no materials left to group.")}</div>
                            )
                            :
                            (
                                <>
                                    {this.state.filteredMaterialList.length === 0 ?
                                        (
                                            <div>{this.localizeString("There are no materials matching the filter.")}</div>
                                        )
                                        :
                                        (
                                            <div id="original-material-table" className="grid-table">
                                                {this.state.filteredMaterialList.map(inv => (
                                                    <div className="original-material item" key={inv.inventoryId}>
                                                        <div className="checkbox-container"><input type="checkbox" checked={this.state.selectedOriginalInventoryIds.has(inv.inventoryId)} onChange={e => this.originalMaterialCheckbox_change(inv.inventoryId)} /></div>
                                                        <div className="name">{`${this.getPartNumberPrefix(inv.inventoryId)}${inv.name.emptyCoalesce(unnamedMaterial)}`}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )
                                    }
                                </>
                            )
                        }
                    </div>
                    <div id="groups-container" className="col-xs-12 col-lg-6">
                        <div className="start-subcontent-heading">{this.localizeString("Material Types")}</div>
                        {(groups.length === 0)
                            ? null
                            : (
                                <div className="grid-table">
                                    {groups
                                        .map((group, index) => (
                                            <div className="group-container item" key={index}>
                                                <div className="name">
                                                    {this.state.allGroupedData!.inventoryInfoTable[group.groupInventoryId ?? ""]?.name.emptyCoalesce(unnamedMaterial) ?? <LoadingSpinner />}
                                                </div>
                                                <div className="material-list">
                                                    {group.responderOwnedResponseInventoryIdList?.map(memberInvId => this.state.allGroupedData!.inventoryInfoTable[memberInvId]?.name).join(", ")}
                                                </div>
                                                <div>
                                                    <button type="button" className="btn btn-default btn-sm" onClick={e => this.editGroup_click(group)}><span className="glyphicon glyphicon-pencil"></span></button>
                                                </div>
                                                <div className="delete-group-container">
                                                    <button type="button" className="btn btn-toxnot-danger btn-sm" onClick={e => this.removeGroupButton_click(group)}><span className="glyphicon glyphicon-remove"></span></button>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            )}
                    </div>
                </div>

                <ToxnotReactModal modalId={"enter-material-group-name-modal"} localizedTitle={this.localizeString("Group")}
                    onClose={() => this.setState({ groupName: null })} >
                    <ToxnotReactModalBody>
                        <>
                            <label>{this.localizeString("Enter a new group name or search for an existing group")}</label>

                            <SearchableDropdown
                                options={groups.map(g => g.groupInventoryId ?? "")}
                                selectedValue={this.state.groupName}
                                onSelect={(newGroupName: string) => this.setState({ groupName: newGroupName })}
                                onSearchTermChanged={(newGroupName: string) => this.setState({ groupName: newGroupName })}
                                getFriendlyNameForOptions={(groupId: string) => { return groups.firstOrDefault(g => g.groupInventoryId == groupId)?.groupName ?? "" }}>
                            </SearchableDropdown>
                        </>
                    </ToxnotReactModalBody>
                    <ToxnotReactModalFooter onCancel={() => this.setState({ groupName: null })} localizer={this.props.appServices} >
                        <button type="button" className="btn btn-toxnot-primary" onClick={e => this.addToGroupModalButton_click(groups)} disabled={this.isNullOrEmpty(this.state.groupName)}>{this.localizeString("Add")}</button>
                    </ToxnotReactModalFooter>
                </ToxnotReactModal>

                <ToxnotReactModal modalId={"edit-group-material-List"} localizedTitle={`${this.localizeString("Materials For")} '${this.state.selectedGroup?.groupName}'`} onClose={this.cancel}>
                    <ToxnotReactModalBody>
                        <>
                            {!this.selectedGroupHasMoreMaterialsThanTreshold() &&
                                <div className="alert alert-danger">
                                    {`${this.localizeString('You cannot delete the materials from ')} ${this.state.selectedGroup?.groupName}. ${this.localizeString('A group should have at least 2 materials')}`}
                                </div>
                            }
                            <div className="grid-table">
                                {this.state.selectedGroup?.responderOwnedResponseInventoryIdList.map(
                                    (materialId, index) => (
                                        <div key={index} className="materials-container group-container item">
                                            <div className="name">
                                                {this.state.allGroupedData!.inventoryInfoTable[materialId].name}
                                            </div>
                                            <div className="delete-group-container">
                                                <button type="button" className="btn btn-toxnot-danger btn-sm" onClick={e => this.removeMaterialFromGroupButton_click(materialId)} disabled={!this.selectedGroupHasMoreMaterialsThanTreshold()}><span className="glyphicon glyphicon-remove"></span></button>
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        </>
                    </ToxnotReactModalBody>
                    <ToxnotReactModalFooter onCancel={this.cancel} localizer={this.props.appServices}>
                    </ToxnotReactModalFooter>
                </ToxnotReactModal>
            </div>
        );
    }

    private cancel = ()=> {
        if (this.state.isItemRemoved) {
            this.props.getPaginatedSurveyData(this.props.navContext.currentPageOfMaterialsIndex);
        }

        $(`#${"edit-group-material-List"}`).modal("hide");
    }


    private getPartNumberPrefix(inventoryId: string) {
        var partNumber = this.state.allGroupedData!.request!.metadata.additionalColumnIdIdentifiers.some(i => i === "supplier-part-number")
            ? this.state.allGroupedData!.requestorTargetLibraryInventoryIdentifiers.firstOrDefault(i => i.inventoryId === inventoryId)?.supplierPartNumber
            : null;

        return partNumber ? `PN${partNumber}: ` : "";
    }

    // ===============================================================================

    private async persistData(groups: GroupedResponseInventories) {
        await this._saveDataManager.saveAsync(groups);
    }

    // ===============================================================================

    private setGroupsState(newGroups: GroupedResponseInventories) {
        // Update both states simultaneously
        const newInventoryInfoTableForNav = { ...this.props.coreData!.inventoryInfoTable };
        const newMaterialIdsDataToLoadListForNav = [...this.props.coreData!.materialIdsDataToLoadList];
        const newInventoryInfoTable = { ...this.state.allGroupedData!.inventoryInfoTable };
        const newMaterialIdsDataToLoadList = [...this.state.allGroupedData!.materialIdsDataToLoadList];

        for (let groupInventoryName of Object.keys(newGroups)) {
            const group = newGroups[groupInventoryName];

            if (group.groupInventoryId !== null) {
                const newInventoryInfo = {
                    ownerId: "",
                    inventoryId: group.groupInventoryId,
                    name: groupInventoryName,
                    description: "",
                    supplierName: "",
                    productNumber: "",
                    supplierPartNumber: "",
                    supplierId: null,
                    plantName: "",
                    isComponent: true 
                };

                // Update both inventory info tables
                newInventoryInfoTable[group.groupInventoryId] = newInventoryInfo;
                newInventoryInfoTableForNav[group.groupInventoryId] = newInventoryInfo;

                // Update both material IDs lists
                if (!newMaterialIdsDataToLoadList.includes(group.groupInventoryId)) {
                    newMaterialIdsDataToLoadList.push(group.groupInventoryId);
                }
                if (!newMaterialIdsDataToLoadListForNav.includes(group.groupInventoryId)) {
                    newMaterialIdsDataToLoadListForNav.push(group.groupInventoryId);
                }
            }
        }

        // Update props (for side nav)
        this.props.setSurveyResponseState({
            coreData: {
                ...this.props.coreData!,
                response: {
                    ...this.props.coreData!.response,
                    groupedResponseInventories: newGroups
                },
                materialIdsDataToLoadList: newMaterialIdsDataToLoadListForNav,
                inventoryInfoTable: newInventoryInfoTableForNav
            }
        });

        // Update local state (for current page)
        this.setState({
            allGroupedData: {
                ...this.state.allGroupedData!,
                response: {
                    ...this.state.allGroupedData!.response,
                    groupedResponseInventories: newGroups
                },
                materialIdsDataToLoadList: newMaterialIdsDataToLoadList,
                inventoryInfoTable: newInventoryInfoTable
            }
        });

        this.setMaterialList(newMaterialIdsDataToLoadList);
    }

    // ===============================================================================

    //
    // UI Events
    //

    private removeGroupButton_click = async (group: SurveyResponseInventoryGroup) => {
        const newGroups = { ...this.state.allGroupedData!.response.groupedResponseInventories };
        let newMaterialIdsDataToLoadList = [...this.state.allGroupedData!.materialIdsDataToLoadList];
        let newMaterialIdsDataToLoadListForNav = [...this.props.coreData!.materialIdsDataToLoadList];

        delete newGroups[group.groupName];

        const newInventoryInfoTable = { ...this.state.allGroupedData!.inventoryInfoTable };
        const newInventoryInfoTableForNav = { ...this.props.coreData!.inventoryInfoTable };

        if (group.groupInventoryId !== null) {
            delete newInventoryInfoTable[group.groupInventoryId];
            delete newInventoryInfoTableForNav[group.groupInventoryId];

            newMaterialIdsDataToLoadList = newMaterialIdsDataToLoadList.filter(i => i != group.groupInventoryId);
            newMaterialIdsDataToLoadListForNav = newMaterialIdsDataToLoadListForNav.filter(i => i != group.groupInventoryId);

            group.responderOwnedResponseInventoryIdList.forEach(initialInventoryId => {
                if (!newMaterialIdsDataToLoadList.some(i => i == initialInventoryId))
                    newMaterialIdsDataToLoadList.push(initialInventoryId);
                if (!newMaterialIdsDataToLoadListForNav.some(i => i == initialInventoryId))
                    newMaterialIdsDataToLoadListForNav.push(initialInventoryId);
            });
        }

        this.props.setSurveyResponseState({
            coreData: {
                ...this.props.coreData!,
                inventoryInfoTable: newInventoryInfoTableForNav,
                materialIdsDataToLoadList: newMaterialIdsDataToLoadListForNav,
            }
        });

        this.setState({
            allSelected: false,
            allGroupedData: {
                ...this.state.allGroupedData!,
                inventoryInfoTable: newInventoryInfoTable,
                materialIdsDataToLoadList: newMaterialIdsDataToLoadList,
            }
        });

        this.setMaterialList(newMaterialIdsDataToLoadList);
        this.clearMaterialSearchTerm();

        if (!await this.saveDataToService(newGroups)) {
            return;
        }

        if (group.groupInventoryId) {
            await this.props.updateValidationResultRemoveGroup(group.responderOwnedResponseInventoryIdList, group.groupInventoryId);
        }
        this.props.getPaginatedSurveyData(this.props.navContext.currentPageOfMaterialsIndex);
    }

    // ===============================================================================

    private originalMaterialCheckbox_change = (inventoryId: string) => {
        const selected = new Set<string>(this.state.selectedOriginalInventoryIds);

        if (selected.has(inventoryId)) {
            selected.delete(inventoryId);
        } else {
            selected.add(inventoryId);
        }

        this.setState({
            selectedOriginalInventoryIds: selected,
            allSelected: this.state.filteredMaterialList.length === selected.size
        });
    }

    // ===============================================================================

    private addToGroupModalButton_click = async (groups: SurveyResponseInventoryGroup[]) => {
        if (groups.some(g => g.groupName === this.state.groupName)) {
            this.addMaterialsToExistingGroup();
        } else if (!this.isNullOrEmpty(this.state.groupName)) {
            this.addMaterialsToNewGroup();
        }

        this.clearMaterialSearchTerm();
    }

    private addMaterialsToNewGroup = async () => {
        if (this.isNullOrEmpty(this.state.groupName)) {
            return;
        }
        const selectedMaterials = Array.from(this.state.selectedOriginalInventoryIds);
        let totalNumberOfSelectedMaterials = selectedMaterials.length - 1;

        if (selectedMaterials.length < 2) {
            alert(this.localizeString("You have to select at least 2 materials to create a new group"));
            return;
        }

        const newGroup: SurveyResponseInventoryGroup = {
            groupName: this.state.groupName!,
            groupInventoryId: null,
            responderOwnedResponseInventoryIdList: Array.from(this.state.selectedOriginalInventoryIds),
            supplierLibraryInventoryId: null,
            publicInventoryId: null,
            sharedMaterialId: null
        };

        const response = this.state.allGroupedData!.response;

        // If there is already a group with this name
        if (Object.keys(response.groupedResponseInventories).some(g => g.toLocaleLowerCase() === newGroup.groupName.toLocaleLowerCase())) {
            alert(this.localizeString("You cannot have 2 groups with the same name."))
            return;
        }

        const newGroups = {
            ...response.groupedResponseInventories,
            [newGroup.groupName]: newGroup
        };

        this.setGroupsState(newGroups);
        this.clearGroupAndSelectedMaterilasAndSetAllSelected();

        this.hideModal("enter-material-group-name-modal");

        if (!await this.saveDataToService(newGroups)) {
            return;
        }

        const newGroupId = Object.keys(this.state.allGroupedData?.response.groupedResponseInventories ?? {}).map(g => this.state.allGroupedData?.response.groupedResponseInventories[g]).firstOrDefault(g => g?.groupName == newGroup.groupName)?.groupInventoryId ?? null;
        
        if (newGroupId) {
            this.props.updateValidationResultAddMaterialsToGroup(selectedMaterials, newGroupId);
        }

        this.refreshPageAfterGrouping(totalNumberOfSelectedMaterials);
    }

    // ===============================================================================

    private addSelectedMaterialsToGroup_click = () => {
        if (this.state.selectedOriginalInventoryIds.size < 1) {
            alert(this.localizeString("Please select at least 1 material"));
            return;
        }

        this.showModal("enter-material-group-name-modal");
    }

    private onSelectAllChanged = (newValue: boolean) => {
        const selected = new Set<string>(this.state.selectedOriginalInventoryIds);

        this.state.filteredMaterialList.map(inv => {
            if (newValue && !selected.has(inv.inventoryId)) {
                selected.add(inv.inventoryId);
            } else if (!newValue && selected.has(inv.inventoryId)) {
                selected.delete(inv.inventoryId);
            }
        });

        this.setState({
            selectedOriginalInventoryIds: selected,
            allSelected: newValue
        });
    }

    private editGroup_click = (group: SurveyResponseInventoryGroup | null) => {
        this.setState({
            selectedGroup: group,
            isItemRemoved: false
        });

        this.showModal("edit-group-material-List");
    }

    private removeMaterialFromGroupButton_click = async (materialId: string | null) => {
        const newGroups = { ...this.state.allGroupedData!.response.groupedResponseInventories };
        const newMaterialIdsDataToLoadList = [...this.state.allGroupedData!.materialIdsDataToLoadList];
        const newMaterialIdsDataToLoadListForNav = [...this.props.coreData!.materialIdsDataToLoadList];

        if (materialId){
            newMaterialIdsDataToLoadList.push(materialId);
            newMaterialIdsDataToLoadListForNav.push(materialId);

            this.props.setSurveyResponseState({
                coreData: {
                    ...this.props.coreData!,
                    materialIdsDataToLoadList: newMaterialIdsDataToLoadListForNav
                }
            });
            this.setState({
                allGroupedData: {
                    ...this.state.allGroupedData!,
                    materialIdsDataToLoadList: newMaterialIdsDataToLoadList
                }
            });
        }

        const updatedGroup = newGroups[this.state.selectedGroup?.groupName ?? ""];
        updatedGroup.responderOwnedResponseInventoryIdList = updatedGroup.responderOwnedResponseInventoryIdList.filter(m => m != materialId);

        if (!await this.saveDataToService(newGroups)) {
            return;
        }

        this.setState({
            allSelected: false,
            selectedGroup: updatedGroup,
            isItemRemoved: true
        });

        this.setMaterialList(newMaterialIdsDataToLoadList);
        this.clearMaterialSearchTerm();

        if (materialId) {
            await this.props.updateValidationResult([materialId]);
        }
    }

    private addMaterialsToExistingGroup = async () => {
        const materialIds = Array.from(this.state.selectedOriginalInventoryIds);
        const newGroups = { ...this.state.allGroupedData!.response.groupedResponseInventories};
        const newMaterialIdsDataToLoadList = [...this.state.allGroupedData!.materialIdsDataToLoadList].filter(materialIdToDisplay => !materialIds?.some(addedMaterialId => addedMaterialId == materialIdToDisplay));
        const newMaterialIdsDataToLoadListForNav = [...this.props.coreData!.materialIdsDataToLoadList].filter(materialIdToDisplay => !materialIds?.some(addedMaterialId => addedMaterialId == materialIdToDisplay));
        const updatedGroup = newGroups[this.state.groupName ?? ""];

        const selectedMaterials = Array.from(this.state.selectedOriginalInventoryIds);
        //Initializing vairables for calculating new page 
        let totalNumberOfSelectedMaterials = selectedMaterials.length;

        if (materialIds) {
            updatedGroup.responderOwnedResponseInventoryIdList = updatedGroup.responderOwnedResponseInventoryIdList.concat(materialIds);
        }

        this.props.setSurveyResponseState({
            coreData: {
                ...this.props.coreData!,
                materialIdsDataToLoadList: newMaterialIdsDataToLoadListForNav
            }
        });
        this.setState({
            allGroupedData: {
                ...this.state.allGroupedData!,
                materialIdsDataToLoadList: newMaterialIdsDataToLoadList
            }
        });

        this.setMaterialList(newMaterialIdsDataToLoadList);
        this.clearGroupAndSelectedMaterilasAndSetAllSelected();

        if (!await this.saveDataToService(newGroups)) {
            return;
        }

        this.hideModal("enter-material-group-name-modal");

        if (updatedGroup.groupInventoryId) {
            await this.props.updateValidationResultAddMaterialsToGroup(materialIds, updatedGroup.groupInventoryId);
        }

        this.refreshPageAfterGrouping(totalNumberOfSelectedMaterials);
    }
    
    private refreshPageAfterGrouping(totalNumberOfSelectedMaterials: number){
        const paginationDetails = coreValues(this.props.coreData);
        let currentPageIndex = this.props.navContext.currentPageOfMaterialsIndex;
        
        //Updating number of inventories and pages after creating a group of inventories.
        let updatedNumberOfInventoriesAfterGrouping = paginationDetails.totalInventoriesAfterGrouping - totalNumberOfSelectedMaterials;
        let updateTotalPages = Math.ceil(updatedNumberOfInventoriesAfterGrouping / surveyResponseMaterialsPerPage) - 1;

        //If the current page doesn't exist after grouping then we should navigate the user to last available page.
        if (currentPageIndex > updateTotalPages) {
            currentPageIndex = updateTotalPages;
        }

        this.props.getPaginatedSurveyData(currentPageIndex);
    }

    private selectedGroupHasMoreMaterialsThanTreshold = () => {
        return this.state.selectedGroup?.responderOwnedResponseInventoryIdList.length != undefined && this.state.selectedGroup?.responderOwnedResponseInventoryIdList.length > 2;
    }

    private clearGroupAndSelectedMaterilasAndSetAllSelected = () => {
        this.setState({
            selectedOriginalInventoryIds: new Set<string>(),
            groupName: null,
            allSelected: false
        });
    }

    private getAllGroupedData = async () => {
        return await this._serviceClient.getCoreSurveyResponseDataForAddedMaterials(this.props.coreData?.response.responseId ?? "");
    }

    private getOriginalMaterialList = () => {
        return this.original.filter(inv => !Object.values(this.state.allGroupedData!.response.groupedResponseInventories)
            .some(g => g.responderOwnedResponseInventoryIdList.includes(inv.inventoryId))
            && this.state.allGroupedData?.materialIdsDataToLoadList.some(id => id == inv.inventoryId));
    }

    private filterMaterialList = (value: string) => {
        const isSearchTermSet = !this.isNullOrEmpty(value);
        const originalMaterialList = this.getOriginalMaterialList();
        const searchTerm = value.toLowerCase();

        const newMaterialList = isSearchTermSet
            ? originalMaterialList.filter(m => {
                const { productNumber, name, supplierPartNumber, description, inventoryId } = m;
                return [productNumber, name, supplierPartNumber, description].some(field =>
                    field.toLowerCase().includes(searchTerm)
                ) || this.inventoryIdentifiersContains(searchTerm, inventoryId)
            })
            : originalMaterialList;

        const selectedMaterials = Array.from(this.state.selectedOriginalInventoryIds);
        const allSelected = newMaterialList.length > 0 && newMaterialList.every(m => selectedMaterials.some(s => s === m.inventoryId));

        this.setState({
            filteredMaterialList: newMaterialList,
            isMaterialListFiltered: isSearchTermSet,
            materialSearchTerm: value,
            allSelected: allSelected
        });
    }

    private inventoryIdentifiersContains(searchTerm: string, inventoryId: string) {
        const searchTermLowerCase = searchTerm.toLowerCase();
        const includedIdentifiers = this.props.coreData!.request!.metadata.additionalColumnIdIdentifiers;
        const identifiers = this.props.coreData!.requestorTargetLibraryInventoryIdentifiers ?? [];
        const inventoryIdentifier = identifiers.firstOrDefault(i => i.inventoryId === inventoryId);

        if (inventoryIdentifier === null) {
            return false;
        }

        return includedIdentifiers.some(id => SurveyStateController.additionalIdentityColumnsIds[id](inventoryIdentifier).toString().toLowerCase().includes(searchTermLowerCase));
    }

    private setMaterialList = (newMaterialIdsDataToLoadList: string[]) => {
        this.setState({
            filteredMaterialList: this.getOriginalMaterialList().filter(m => newMaterialIdsDataToLoadList.includes(m.inventoryId) && (this.isNullOrEmpty(this.state.materialSearchTerm) || m.name.toLowerCase().includes(this.state.materialSearchTerm.toLowerCase())))
        });
    }

    private clearMaterialSearchTerm = () => {
        this.setState({
            materialSearchTerm: ""
        });
    }
}

// ===============================================================================

//
// Connect to redux store & survey response state controller
//
export default connect(
    (state: ApplicationState) => ({ ...state.surveyResponseState, appServices: state.appServices }),
    SurveyResponseController.actionCreators
)(GroupMaterials as any);
